import * as React from "react";
import { useMemo, useRef, useState } from "react";
import { betreffOhneAntwortPraefix } from "../../../utils/betreffPraefix";
import { betreffOhneKontaktKopf } from "../../../utils/betreffKontaktKopf";

/**
 * Das Betreff-Feld der Vorgangsanlage — mit dem Mail-Betreff als Pille IM Feld.
 *
 * PO 28.09.2026: „der Betreff ausfüllen aus Mail Button, nicht über dem Text,
 * sondern als Bestandteil des Inputs, also [Text der lang ist … ×]". Bis dahin
 * stand ein Stift-Knopf rechts oben am Feld; was er einsetzen würde, sah man
 * erst nach dem Klick.
 *
 * Ist das Feld leer und hat die Mail einen Betreff, steht rechts im Feld eine
 * Pille mit dem Mail-Betreff (abgeschnitten mit „…"). Ein Klick schreibt ihn
 * als normalen Text ins Feld, der Cursor steht am Ende. Vorbelegt wird dabei
 * weiterhin NICHTS (C3) — wer tippt, überschreibt das Angebot einfach, die
 * Pille verschwindet; wird das Feld wieder geleert, ist sie wieder da.
 *
 * T-47 (30.09.2026): übernommen steht der Betreff als normaler Text im Feld —
 * der Chip-Zustand aus #1829 (Pille mit „×") war nicht gewünscht.
 *
 * Der Rest bleibt wie beim Stift: ohne „AW:"/„WG:"-Stapel (E5/E6), und wenn
 * danach nichts übrig bleibt, ein Satz statt eines stillen No-Ops (A9).
 *
 * T-46 (30.09.2026), verschärft 01.10.2026: Der Teil des Mail-Betreffs bis
 * einschließlich zum ersten „/" („Kundenname/ Kunde Betreff") fällt aus
 * Angebot UND Übernahme IMMER weg — auch wenn noch kein Kontakt gewählt ist.
 * Nur beim Courté-Schema („… - VSNR 4711 / …") gilt weiter der Namensvergleich
 * mit den zugewiesenen Kontakten — siehe `betreffOhneKontaktKopf`.
 */
export const BetreffFeld: React.FC<{
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    autoFocus?: boolean;
    /** Betreff der Quell-Mail (schon ohne Kunden-Klammer) — null = kein Angebot. */
    mailBetreff?: string | null;
    /** Namensvarianten der zugewiesenen Kontakte (T-46) — nur noch für das
     *  Courté-Schema („Name - VSNR …") gebraucht; „bis zum ersten /" gilt immer. */
    kontaktNamen?: readonly string[];
}> = ({ value, onChange, placeholder, autoFocus, mailBetreff, kontaktNamen }) => {
    const [hinweis, setHinweis] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const angebot = useMemo(
        () => betreffOhneKontaktKopf(mailBetreff, kontaktNamen ?? []).trim() || null,
        [mailBetreff, kontaktNamen],
    );
    const zeigeAngebot = !!angebot && !value.trim();

    const uebernehmen = () => {
        const rein = betreffOhneAntwortPraefix(angebot ?? "");
        if (rein) {
            onChange(rein);
            setHinweis(false);
            // Der neue Wert steht erst im nächsten Render im Feld.
            requestAnimationFrame(() => {
                const el = inputRef.current;
                if (!el) return;
                el.focus();
                el.setSelectionRange(el.value.length, el.value.length);
            });
        } else {
            setHinweis(true);
        }
    };

    return (
        <>
            <div className="vgc-betreff">
                <input
                    ref={inputRef}
                    className="vgd-input vgc-betreff-input"
                    aria-label="Betreff"
                    autoFocus={autoFocus}
                    placeholder={zeigeAngebot ? "" : placeholder}
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setHinweis(false);
                    }}
                />
                {zeigeAngebot && (
                    <button
                        type="button"
                        className="vgc-betreff-angebot"
                        aria-label="Betreff aus der Mail übernehmen"
                        title={`Betreff aus der Mail übernehmen: ${angebot}`}
                        onClick={uebernehmen}
                    >
                        <span className="vgc-betreff-angebot-art">aus Mail</span>
                        <span className="vgc-betreff-angebot-text">{angebot}</span>
                    </button>
                )}
            </div>
            {hinweis && (
                <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", marginTop: 4 }}>
                    Die Mail hat keinen übernehmbaren Betreff.
                </div>
            )}
        </>
    );
};
